import React from 'react';
import { Loader2 } from 'lucide-react';
import { Skeleton } from '../../ui/skeleton';
import { cn } from '../../../lib/utils';

// ============================================================================
// LoadingSpinner - Spinner genérico para loading states
// ============================================================================

interface ILoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  text?: string;
}

export const LoadingSpinner: React.FC<ILoadingSpinnerProps> = ({
  size = 'md',
  className,
  text,
}) => {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-8 w-8',
    lg: 'h-12 w-12',
    xl: 'h-16 w-16',
  };

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 p-4',
        className,
      )}
    >
      <Loader2
        className={cn('animate-spin', sizeClasses[size])}
        style={{ color: 'var(--primary-orange)' }}
      />
      {text && (
        <p className="text-sm" style={{ color: 'var(--neutral-grey)' }}>
          {text}
        </p>
      )}
    </div>
  );
};

// ============================================================================
// TableSkeleton - Skeleton loading para tabelas
// ============================================================================

interface ITableSkeletonProps {
  rows?: number;
  cols?: number;
  className?: string;
}

export const TableSkeleton: React.FC<ITableSkeletonProps> = ({
  rows = 5,
  cols = 6,
  className,
}) => {
  return (
    <div className={cn('space-y-3', className)}>
      {/* Header row */}
      <div
        className="flex gap-4 pb-2 border-b"
        style={{ borderColor: 'var(--neutral-beige)' }}
      >
        {/* Justificado: Array.from para skeleton loaders - sem dados reais, ordem estável */}
        {/* eslint-disable react/no-array-index-key */}
        {Array.from({ length: cols }).map((_unused, i) => (
          <Skeleton key={`header-${i}`} className="h-4 flex-1" />
        ))}
        {/* eslint-enable react/no-array-index-key */}
      </div>

      {/* Data rows - Justificado: Array.from para skeleton loaders - sem dados reais, ordem estável */}
      {/* eslint-disable react/no-array-index-key */}
      {Array.from({ length: rows }).map((__unused, rowIndex) => (
        <div key={`row-${rowIndex}`} className="flex gap-4">
          {Array.from({ length: cols }).map((___unused, colIndex) => (
            <Skeleton
              key={`cell-${rowIndex}-${colIndex}`}
              className="h-4 flex-1"
            />
          ))}
        </div>
      ))}
      {/* eslint-enable react/no-array-index-key */}
    </div>
  );
};

// ============================================================================
// PaginationSkeleton - Skeleton loading para paginação
// ============================================================================

interface IPaginationSkeletonProps {
  className?: string;
}

export const PaginationSkeleton: React.FC<IPaginationSkeletonProps> = ({
  className,
}) => {
  return (
    <div className={cn('flex items-center justify-between gap-4', className)}>
      {/* Info text skeleton */}
      <Skeleton className="h-4 w-32" />

      {/* Pagination buttons skeleton */}
      <div className="flex gap-2">
        <Skeleton className="h-8 w-8" />
        <Skeleton className="h-8 w-8" />
        <Skeleton className="h-8 w-8" />
        <Skeleton className="h-8 w-8" />
        <Skeleton className="h-8 w-8" />
      </div>

      {/* Per page selector skeleton */}
      <Skeleton className="h-8 w-24" />
    </div>
  );
};

// Aliases for backward compatibility
export const LoadingInsidePagination = LoadingSpinner;
export const TableSkeletonLoader = TableSkeleton;

// ============================================================================
// FullPageLoader - Loading de página inteira (substitui SplashScreen/DevyxLoading)
// ============================================================================

interface IFullPageLoaderProps {
  text?: string;
}

export const FullPageLoader: React.FC<IFullPageLoaderProps> = ({
  text = 'Carregando...',
}) => {
  return (
    <div
      className="fixed inset-0 flex flex-col items-center justify-center z-50"
      style={{ background: 'var(--neutral-off-white)' }}
    >
      <div className="flex flex-col items-center gap-4">
        <Loader2
          className="h-16 w-16 animate-spin"
          style={{ color: 'var(--primary-orange)' }}
        />
        <p
          className="text-lg font-medium"
          style={{ color: 'var(--primary-dark)' }}
        >
          {text}
        </p>
      </div>
    </div>
  );
};

// ============================================================================
// CardSkeleton - Skeleton para cards
// ============================================================================

interface ICardSkeletonProps {
  className?: string;
  hasImage?: boolean;
}

export const CardSkeleton: React.FC<ICardSkeletonProps> = ({
  className,
  hasImage = false,
}) => {
  return (
    <div
      className={cn('rounded-lg border p-6 space-y-4', className)}
      style={{ borderColor: 'var(--neutral-beige)' }}
    >
      {hasImage && <Skeleton className="h-48 w-full rounded-md" />}
      <Skeleton className="h-6 w-3/4" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
      <div className="flex gap-2 pt-4">
        <Skeleton className="h-8 w-20" />
        <Skeleton className="h-8 w-20" />
      </div>
    </div>
  );
};
